/**
 * Marketing Hub — drop-in routes module.
 *
 * Usage in your root router (App.js):
 *
 *   import { MarketingRoutes } from "./marketing-hub.routes";
 *   ...
 *   <Routes>
 *     ...your CRM routes...
 *     {MarketingRoutes({ ProtectedRoute, MainLayout })}
 *   </Routes>
 *
 * `ProtectedRoute` + `MainLayout` are YOUR existing wrappers — pass them in so
 * marketing routes inherit your auth gate and sidebar/topbar chrome.
 *
 * Toggle the module entirely off by setting REACT_APP_ENABLE_MARKETING=false.
 */
import React from "react";
import { Route, Navigate } from "react-router-dom";

import MarketingModeBar from "@/components/marketing/MarketingModeBar";
import { OLD_TO_NEW } from "@/lib/marketingIA";

// Pages
import MarketingOverview   from "@/pages/marketing/MarketingOverview";
import MarketingCalendar   from "@/pages/marketing/MarketingCalendar";
import MarketingCampaigns  from "@/pages/marketing/MarketingCampaigns";
import ContentStudio       from "@/pages/marketing/ContentStudio";
import TemplateLibrary     from "@/pages/marketing/TemplateLibrary";
import BrandLibrary        from "@/pages/marketing/BrandLibrary";
import AssetGenerator      from "@/pages/marketing/AssetGenerator";
import Channels            from "@/pages/marketing/Channels";
import Approvals           from "@/pages/marketing/Approvals";
import Connections         from "@/pages/marketing/Connections";
import MarketingAnalytics  from "@/pages/marketing/MarketingAnalytics";
import GeoSeoEngine        from "@/pages/marketing/GeoSeoEngine";
import MarketingReports    from "@/pages/marketing/MarketingReports";
import Agents              from "@/pages/marketing/Agents";
import Workflows           from "@/pages/marketing/Workflows";
import RunHistory          from "@/pages/marketing/RunHistory";

// /marketing/plan/segments — by default reuse your CRM LeadSegments page.
// If your project doesn't have one, swap for any list page or remove the route.
let LeadSegments = null;
try {
  // eslint-disable-next-line global-require
  LeadSegments = require("@/pages/LeadSegments").default;
} catch (e) {
  LeadSegments = () => (
    <div style={{ padding: 40 }}>
      Wire your "segments" / audience-builder page here.
    </div>
  );
}

const MARKETING_ENABLED = String(process.env.REACT_APP_ENABLE_MARKETING || "true")
  .toLowerCase() === "true";

/** Wraps any marketing route with the mode bar + your existing chrome. */
function MP({ element, ProtectedRoute, MainLayout, Gate }) {
  const inner = (
    <>
      <MarketingModeBar />
      {element}
    </>
  );
  return (
    <ProtectedRoute>
      <MainLayout>
        {Gate ? <Gate>{inner}</Gate> : inner}
      </MainLayout>
    </ProtectedRoute>
  );
}

export function MarketingRoutes({ ProtectedRoute, MainLayout, Gate }) {
  if (!MARKETING_ENABLED) {
    return <Route path="/marketing/*" element={<Navigate to="/" replace />} />;
  }
  const wrap = (element) => (
    <MP element={element} ProtectedRoute={ProtectedRoute} MainLayout={MainLayout} Gate={Gate} />
  );
  return (
    <>
      {/* PLAN */}
      <Route path="/marketing/plan/overview"  element={wrap(<MarketingOverview />)} />
      <Route path="/marketing/plan/calendar"  element={wrap(<MarketingCalendar />)} />
      <Route path="/marketing/plan/segments"  element={wrap(<LeadSegments />)} />
      <Route path="/marketing/plan/campaigns" element={wrap(<MarketingCampaigns />)} />
      {/* CREATE */}
      <Route path="/marketing/create/studio"    element={wrap(<ContentStudio />)} />
      <Route path="/marketing/create/templates" element={wrap(<TemplateLibrary />)} />
      <Route path="/marketing/create/library"   element={wrap(<BrandLibrary />)} />
      <Route path="/marketing/create/assets"    element={wrap(<AssetGenerator />)} />
      {/* DISTRIBUTE */}
      <Route path="/marketing/distribute/channels"    element={wrap(<Channels />)} />
      <Route path="/marketing/distribute/approvals"   element={wrap(<Approvals />)} />
      <Route path="/marketing/distribute/connections" element={wrap(<Connections />)} />
      {/* MEASURE */}
      <Route path="/marketing/measure/analytics" element={wrap(<MarketingAnalytics />)} />
      <Route path="/marketing/measure/geo-seo"   element={wrap(<GeoSeoEngine />)} />
      <Route path="/marketing/measure/reports"   element={wrap(<MarketingReports />)} />
      {/* AGENTS */}
      <Route path="/marketing/agents/list"      element={wrap(<Agents />)} />
      <Route path="/marketing/agents/workflows" element={wrap(<Workflows />)} />
      <Route path="/marketing/agents/runs"      element={wrap(<RunHistory />)} />

      {/* Legacy flat URLs → new IA (client-side 301-equivalents). */}
      {Object.entries(OLD_TO_NEW).map(([oldPath, newPath]) => (
        <Route key={oldPath} path={oldPath} element={<Navigate to={newPath} replace />} />
      ))}
      <Route path="/marketing" element={<Navigate to="/marketing/plan/overview" replace />} />
    </>
  );
}

export default MarketingRoutes;
